import React, { useEffect, useMemo, useState } from "react";

import {
  OnePagePortfolioAssessmentAnswer,
  OnePagePortfolioAssessment,
  ReferenceIssues,
} from "../../../state";
import Modal from "../../../atoms/Modal";
import Button from "../../../atoms/ActiveLearning/Button";
import BAWorkGuideModal from "../../teacher/BAWorkGuideModal";
import BAWorkAnswersModal from "../../../templates/teacher/BAWorkAnswersModal";
import { ReactComponent as QuestionMark } from "../../../assets/svg/before_after_work_guide_question.svg";
import { ReactComponent as CloseIcon } from "../../../assets/svg/oppa-after-modal_close.svg";

import Question from "./Question";
import Reference from "./Reference";
import styles from "./AfterModal.module.scss";

export type AfterModalProps = {
  isTeacher?: boolean;
  classList?: number[];
  assessmentId: number;
  assessmentData: OnePagePortfolioAssessment | null;
  referenceIssues: ReferenceIssues[];
  calling?: boolean;
  isRegisterOPPAAnswersSuccess?: boolean;
  canViewEveryoneAnswers?: boolean;
  fetchOnePagePortfolioAssessment?: (id: number) => void;
  registerOnePagePortfolioAssessmentAnswers?: (
    id: number,
    references: OnePagePortfolioAssessmentAnswer[],
  ) => void;
  onNext: () => void;
  onClose: () => void;
};

const AfterModal: React.FC<AfterModalProps> = ({
  isTeacher,
  classList,
  assessmentId,
  assessmentData,
  referenceIssues,
  calling,
  isRegisterOPPAAnswersSuccess,
  canViewEveryoneAnswers,
  fetchOnePagePortfolioAssessment,
  registerOnePagePortfolioAssessmentAnswers,
  onNext,
  onClose,
}) => {
  const [answer01, setAnswer01] = useState<string>("");
  const [answer02, setAnswer02] = useState<string>("");
  const [showGuideModal, setShowGuideModal] = useState(false);
  const [showAnswersModal, setShowAnswersModal] = useState(false);
  const [pendingNext, setPendingNext] = useState(false);

  useEffect(() => {
    if (!fetchOnePagePortfolioAssessment) return;

    fetchOnePagePortfolioAssessment(assessmentId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentId]);

  const assessmentTitle = useMemo(() => {
    const title = assessmentData?.title ?? "";
    return title.replace(/\s*-高校-|\s*-中学-/g, "");
  }, [assessmentData]);

  const referenceTitleBeforeAnswer = useMemo(() => {
    return (
      assessmentData?.reference_one_page_portfolio_assessment_issues[0].title ??
      ""
    );
  }, [assessmentData]);

  const question01 = useMemo(() => {
    const issue = assessmentData?.one_page_portfolio_assessment_issues?.[0];
    const question = issue?.one_page_portfolio_assessment_questions?.[0];
    return {
      id: question?.id ?? 0,
      title: question?.title ?? "",
      answer: question?.one_page_portfolio_assessment_answer?.contents ?? "",
    };
  }, [assessmentData]);

  const question02 = useMemo(() => {
    const issue = assessmentData?.one_page_portfolio_assessment_issues?.[1];
    const question = issue?.one_page_portfolio_assessment_questions?.[0];

    return {
      id: question?.id ?? 0,
      title: question?.title ?? "",
      answer: question?.one_page_portfolio_assessment_answer?.contents ?? "",
    };
  }, [assessmentData]);

  useEffect(() => {
    setAnswer01(question01.answer);
    setAnswer02(question02.answer);
  }, [question01.answer, question02.answer]);

  const checkAnswers = useMemo(() => {
    const hasChanges =
      answer01 !== question01.answer || answer02 !== question02.answer;
    const isEmpty = !answer01.trim() && !answer02.trim();

    return hasChanges && !isEmpty;
  }, [answer01, answer02, question01.answer, question02.answer]);

  const handleSubmit = () => {
    if (!registerOnePagePortfolioAssessmentAnswers) return;

    const assessmentAnswer: OnePagePortfolioAssessmentAnswer[] = [
      {
        one_page_portfolio_assessment_question_id: question01.id,
        contents: answer01,
      },
      {
        one_page_portfolio_assessment_question_id: question02.id,
        contents: answer02,
      },
    ];
    registerOnePagePortfolioAssessmentAnswers(assessmentId, assessmentAnswer);
  };

  const handleClose = () => {
    setPendingNext(false);
    if (!isTeacher && checkAnswers) handleSubmit();
    onClose();
  };

  useEffect(() => {
    if (!pendingNext || !isRegisterOPPAAnswersSuccess) return;
    onClose();
    onNext();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRegisterOPPAAnswersSuccess, pendingNext]);

  const handleNext = () => {
    if (!isTeacher && checkAnswers) {
      setPendingNext(true);
      handleSubmit();
    } else {
      onClose();
      onNext();
    }
  };

  return (
    <>
      {showGuideModal && (
        <BAWorkGuideModal onCloseModal={() => setShowGuideModal(false)} />
      )}
      {showAnswersModal && (
        <BAWorkAnswersModal
          oppaId={assessmentId}
          classList={classList ?? []}
          onCloseModal={() => setShowAnswersModal(false)}
        />
      )}
      <Modal onClose={handleClose} className={styles.afterModal}>
        <CloseIcon className={styles.closeIcon} onClick={handleClose} />
        <div className={styles.afterModalContainer}>
          <div className={styles.header}>
            <div className={styles.titleWrapper}>
              <p className={styles.title}>{assessmentTitle}</p>
              {isTeacher && (
                <div
                  className={styles.questionMarkWrapper}
                  onClick={() => setShowGuideModal(true)}
                >
                  <QuestionMark />
                </div>
              )}
            </div>
            <p className={styles.annotation}>※回答は先生にのみ送信されます。</p>
          </div>
          <div className={styles.section}>
            <Question
              no="①"
              title={question01.title}
              value={answer01}
              isTeacher={isTeacher}
              onChange={e => setAnswer01(e.target.value)}
            />
            <Reference
              title="振り返りワークの回答"
              referenceData={referenceIssues}
            />
          </div>
          <div className={styles.section}>
            <Question
              no="②"
              title={question02.title}
              value={answer02}
              isTeacher={isTeacher}
              onChange={e => setAnswer02(e.target.value)}
            />
            <Reference
              title={referenceTitleBeforeAnswer}
              referenceData={
                assessmentData?.reference_one_page_portfolio_assessment_issues ??
                []
              }
            />
          </div>
          <div className={styles.buttonArea}>
            {isTeacher ? (
              <Button
                disabled={!canViewEveryoneAnswers}
                handleClick={() => {
                  setShowAnswersModal(true);
                }}
              >
                みんなの回答を見る
              </Button>
            ) : (
              <Button
                handleClick={() => {
                  setPendingNext(false);
                  handleSubmit();
                }}
                disabled={calling}
              >
                回答を送信する
              </Button>
            )}
            <Button
              handleClick={handleNext}
              variant="secondaryBgWhite"
              disabled={calling}
            >
              次のページへ進む
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default AfterModal;
